import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { installerService } from '../../services/installerService';
import './SimplePromotions.css';

const SimplePromotions = () => {
  const [activePromotions, setActivePromotions] = useState([]);
  const [expiredPromotions, setExpiredPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      setLoading(true);
      
      // Load both active and expired promotions
      const [activeResponse, expiredResponse] = await Promise.all([
        installerService.getActivePromotions(),
        installerService.getExpiredPromotions()
      ]);
      
      setActivePromotions(activeResponse.data.promotions || []);
      setExpiredPromotions(expiredResponse.data.promotions || []);
    } catch (error) {
      console.error('Failed to load promotions:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getDaysRemaining = (endDate) => {
    const now = new Date();
    const end = new Date(endDate);
    const diffTime = end - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  };

  const getPromotionsToShow = () => {
    return activeTab === 'active' ? activePromotions : expiredPromotions;
  };

  const promotionsToShow = getPromotionsToShow();

  return (
    <Layout>
      <div className="installer-promotions">
      <div className="promotions-header">
        <h1>Promotions</h1>
        <p>View available and expired promotions</p>
      </div>

      {/* Tabs */}
      <div className="promotions-tabs">
        <button 
          className={`tab ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          <span className="tab-icon">🎯</span>
          Active ({activePromotions.length})
        </button>
        <button 
          className={`tab ${activeTab === 'expired' ? 'active' : ''}`}
          onClick={() => setActiveTab('expired')}
        >
          <span className="tab-icon">📋</span>
          Expired ({expiredPromotions.length})
        </button>
      </div>

      {/* Content */}
      <div className="promotions-content">
        {loading ? (
          <div className="loading">
            <div className="loading-spinner"></div>
            <p>Loading promotions...</p>
          </div>
        ) : (
          <div className="promotions-grid">
            {promotionsToShow.length === 0 ? (
              <div className="no-promotions">
                <div className="no-promotions-icon">
                  {activeTab === 'active' ? '🎁' : '📝'}
                </div>
                <h3>No {activeTab} promotions</h3>
                <p>
                  {activeTab === 'active' 
                    ? 'There are currently no active promotions available.' 
                    : 'No expired promotions to show.'}
                </p>
              </div>
            ) : (
              promotionsToShow.map(promotion => (
                <div key={promotion._id} className={`promotion-card ${activeTab}`}>
                  <div className="promotion-header">
                    <h3 className="promotion-title">{promotion.title}</h3>
                    {activeTab === 'active' && (
                      <div className="promotion-status active">
                        <span className="status-dot"></span>
                        Active
                      </div>
                    )}
                    {activeTab === 'expired' && (
                      <div className="promotion-status expired">
                        <span className="status-dot"></span>
                        Expired
                      </div>
                    )}
                  </div>
                  
                  <div className="promotion-description">
                    <p>{promotion.description}</p>
                  </div>

                  <div className="promotion-details">
                    <div className="detail-item">
                      <span className="detail-label">🎯 Target:</span>
                      <span className="detail-value">{promotion.target || 'Not specified'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">🎁 Reward:</span>
                      <span className="detail-value">{promotion.reward || 'Not specified'}</span>
                    </div>
                  </div>
                  
                  <div className="promotion-dates">
                    <div className="date-item">
                      <span className="date-label">Start Date:</span>
                      <span className="date-value">{formatDate(promotion.startDate)}</span>
                    </div>
                    <div className="date-item">
                      <span className="date-label">End Date:</span>
                      <span className="date-value">{formatDate(promotion.endDate)}</span>
                    </div>
                  </div>
                  
                  {activeTab === 'active' && (
                    <div className="promotion-footer">
                      <div className="days-remaining">
                        <span className="days-number">{getDaysRemaining(promotion.endDate)}</span>
                        <span className="days-text">days remaining</span>
                      </div>
                    </div>
                  )}
                  
                  {activeTab === 'expired' && (
                    <div className="promotion-footer expired">
                      <div className="expired-badge">
                        <span>Promotion Ended</span>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
    </Layout>
  );
};

export default SimplePromotions;
